export type ColorFormat = 'hsl' | 'oklch';

export function detectColorFormat(value: string): ColorFormat {
    const trimmed = value.trim();
    const parts = trimmed.split(/\s+/);
    // HSL values have % signs in lightness and saturation (e.g., "256 88% 65%")
    if (parts.some((p) => p.endsWith('%'))) return 'hsl';
    // OKLCH values are small decimals without % (e.g., "0.21 0.006 285.885")
    return 'oklch';
}

function clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
}

// --- HSL conversions ---

export function hslStringToHex(hsl: string): string {
    const parts = hsl.trim().split(/\s+/);
    if (parts.length < 3) return '#000000';

    const h = (((parseFloat(parts[0]) || 0) % 360) + 360) % 360;
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
        g = 0,
        b = 0;
    if (h < 60) {
        r = c;
        g = x;
    } else if (h < 120) {
        r = x;
        g = c;
    } else if (h < 180) {
        g = c;
        b = x;
    } else if (h < 240) {
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        b = c;
    } else {
        r = c;
        b = x;
    }

    const toHex = (v: number) =>
        clamp(Math.round((v + m) * 255), 0, 255)
            .toString(16)
            .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToHslString(hex: string): string {
    const { r, g, b } = hexToRgb(hex);
    const rf = r / 255;
    const gf = g / 255;
    const bf = b / 255;

    const max = Math.max(rf, gf, bf);
    const min = Math.min(rf, gf, bf);
    const d = max - min;
    const l = (max + min) / 2;

    let h = 0;
    let s = 0;

    if (d > 0) {
        s = d / (1 - Math.abs(2 * l - 1));
        if (max === rf) h = ((gf - bf) / d + 6) % 6;
        else if (max === gf) h = (bf - rf) / d + 2;
        else h = (rf - gf) / d + 4;
        h *= 60;
    }

    return `${Math.round(h * 10) / 10} ${Math.round(s * 1000) / 10}% ${Math.round(l * 1000) / 10}%`;
}

// --- Luminance / contrast ---

// WCAG relative luminance for an HSL string ("H S% L%"). Returns null if the
// string can't be parsed as an HSL triple.
export function hslLuminance(hsl: string): number | null {
    const parts = hsl.trim().split(/\s+/);
    if (parts.length < 3) return null;
    if (!Number.isFinite(parseFloat(parts[0]))) return null;
    if (!Number.isFinite(parseFloat(parts[1]))) return null;
    if (!Number.isFinite(parseFloat(parts[2]))) return null;

    const { r, g, b } = hexToRgb(hslStringToHex(hsl));
    return 0.2126 * linearize(r / 255) + 0.7152 * linearize(g / 255) + 0.0722 * linearize(b / 255);
}

// WCAG contrast ratio between two HSL colors. Returns null if either is unparseable.
export function hslContrastRatio(a: string, b: string): number | null {
    const la = hslLuminance(a);
    const lb = hslLuminance(b);
    if (la === null || lb === null) return null;
    const lighter = Math.max(la, lb);
    const darker = Math.min(la, lb);
    return (lighter + 0.05) / (darker + 0.05);
}

// --- OKLCH conversions ---

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const raw = hex.replace('#', '');
    const h = raw.length === 3 ? raw.replace(/./g, '$&$&') : raw;
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16)
    };
}

function linearize(c: number): number {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function delinearize(c: number): number {
    return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

export function oklchStringToHex(oklch: string): string {
    const parts = oklch.trim().split(/\s+/);
    if (parts.length < 3) return '#000000';

    const L = parseFloat(parts[0]);
    const C = parseFloat(parts[1]);
    const H = parseFloat(parts[2]);

    // OKLCH -> OKLab
    const hRad = (H * Math.PI) / 180;
    const a = C * Math.cos(hRad);
    const b = C * Math.sin(hRad);

    // OKLab -> linear sRGB
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.291485548 * b;

    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;

    const rLin = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    const gLin = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    const bLin = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

    const toHex = (v: number) =>
        clamp(Math.round(delinearize(clamp(v, 0, 1)) * 255), 0, 255)
            .toString(16)
            .padStart(2, '0');
    return `#${toHex(rLin)}${toHex(gLin)}${toHex(bLin)}`;
}

export function hexToOklchString(hex: string): string {
    const { r, g, b } = hexToRgb(hex);

    const rLin = linearize(r / 255);
    const gLin = linearize(g / 255);
    const bLin = linearize(b / 255);

    // linear sRGB -> OKLab
    const l_ = Math.cbrt(0.4122214708 * rLin + 0.5363325363 * gLin + 0.0514459929 * bLin);
    const m_ = Math.cbrt(0.2119034982 * rLin + 0.6806995451 * gLin + 0.1073969566 * bLin);
    const s_ = Math.cbrt(0.0883024619 * rLin + 0.2817188376 * gLin + 0.6299787005 * bLin);

    const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
    const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
    const bOk = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;

    const C = Math.sqrt(a * a + bOk * bOk);
    let H = (Math.atan2(bOk, a) * 180) / Math.PI;
    if (H < 0) H += 360;

    const round = (v: number, d: number) => Math.round(v * Math.pow(10, d)) / Math.pow(10, d);
    return `${round(L, 3)} ${round(C, 3)} ${round(H, 3)}`;
}

export function colorStringToHex(value: string): string {
    if (!value?.trim()) return '#000000';
    return detectColorFormat(value) === 'hsl' ? hslStringToHex(value) : oklchStringToHex(value);
}

export function hexToColorString(hex: string, format: ColorFormat): string {
    return format === 'hsl' ? hexToHslString(hex) : hexToOklchString(hex);
}
